import React from 'react'
import Card from '@mui/material/Card'
import {
  IAllSalesTeam,
  IGroupedSalesTeam,
  ISalesTeamUser,
} from '@user/store/users'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import MenuItem from '@mui/material/MenuItem'
import { Select } from '@workwave-tidal/core/components'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import Chip from '@mui/material/Chip'
import FormHelperText from '@mui/material/FormHelperText'
import { DensityType } from '@user/components/Opportunities/AddOpportunityPage/typings'

type Props = {
  groupedSalesTeams: IGroupedSalesTeam[]
  reAssignedTeams: IGroupedSalesTeam[]
  usersByTeamId: Record<number, ISalesTeamUser[]>
  loadingTeamIds: Record<number, boolean>
  onChangeSalesTeam: (
    salesTeam: IAllSalesTeam | null,
    branchIndex: number,
    salesTeamIndex: number,
  ) => void
  onChangeOwner: (
    user: ISalesTeamUser | null,
    branchIndex: number,
    salesTeamIndex: number,
  ) => void
}

export function BranchCard(props: Props) {
  const {
    groupedSalesTeams,
    onChangeSalesTeam,
    reAssignedTeams,
    usersByTeamId,
    loadingTeamIds,
    onChangeOwner,
  } = props
  const tidalThemeActive = useTidalThemeActive()

  const density: DensityType = tidalThemeActive ? 'normal' : 'compact'

  return (
    <Stack gap="14px">
      {groupedSalesTeams.map((groupedSalesTeam, branchIndex) => (
        <Card
          key={groupedSalesTeam.branchName}
          variant="outlined"
          sx={{
            width: '100%',
            paddingX: '20px',
            paddingY: '17px',
            backgroundColor: '#F9FAFB',
          }}
        >
          <Typography variant="h7">{groupedSalesTeam.branchName}</Typography>

          <Stack gap="12px" mt="16px">
            {groupedSalesTeam.filteredSalesTeams.map(
              (filteredSalesTeam, salesTeamIndex) => {
                const selectedTeamId =
                  reAssignedTeams[branchIndex]?.filteredSalesTeams[
                    salesTeamIndex
                  ]?.id
                const isLoadingUsers = Boolean(
                  selectedTeamId && loadingTeamIds[selectedTeamId],
                )

                return (
                  <Card
                    key={filteredSalesTeam.id}
                    variant="outlined"
                    sx={{
                      width: '100%',
                      paddingX: '20px',
                      paddingY: '17px',
                      backgroundColor: '#FFFFFF',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle3">
                        {filteredSalesTeam.name}
                      </Typography>

                      <Chip
                        size={'small'}
                        label={
                          filteredSalesTeam.openRecords +
                          ` Open Record${
                            Number(filteredSalesTeam.openRecords) > 1 ? 's' : ''
                          }`
                        }
                        style={{
                          backgroundColor: '#D4E3F3',
                          borderRadius: '4px',
                          border: '1px solid #1565C0',
                        }}
                      />
                    </Stack>

                    <Stack
                      direction={{ sm: 'column', md: 'row' }}
                      mt="20px"
                      justifyContent="space-between"
                      gap="15px"
                    >
                      <Stack
                        sx={{
                          width: { sm: '100%', md: 'calc(50% - 7.5px)' },
                        }}
                      >
                        <Select<IAllSalesTeam | null>
                          getLabel={(option: IAllSalesTeam) =>
                            option?.name || ''
                          }
                          items={groupedSalesTeam.allSalesTeams}
                          density={density}
                          onChange={(value) => {
                            if (value) {
                              onChangeSalesTeam(
                                value,
                                branchIndex,
                                salesTeamIndex,
                              )
                            }
                          }}
                          value={
                            reAssignedTeams[branchIndex]?.filteredSalesTeams[
                              salesTeamIndex
                            ] || null
                          }
                          required={true}
                          optionMode={'custom'}
                          renderOption={(props, team: IAllSalesTeam) => (
                            <MenuItem {...props} key={team.id} value={team.id}>
                              <Typography variant="body1">
                                {team.name}
                              </Typography>
                            </MenuItem>
                          )}
                          label="Sales Team"
                        />
                      </Stack>

                      <Stack
                        sx={{
                          width: { sm: '100%', md: 'calc(50% - 7.5px)' },
                          alignItems: { sm: 'flex-start', md: 'center' },
                        }}
                      >
                        <Select<ISalesTeamUser | null>
                          getLabel={(option: ISalesTeamUser) =>
                            option?.fullName || ''
                          }
                          density={density}
                          disabled={isLoadingUsers}
                          placeholder={
                            isLoadingUsers ? 'Loading users...' : undefined
                          }
                          items={
                            selectedTeamId
                              ? usersByTeamId[selectedTeamId] || []
                              : []
                          }
                          onChange={(value) => {
                            onChangeOwner(value, branchIndex, salesTeamIndex)
                          }}
                          value={
                            reAssignedTeams[branchIndex]?.filteredSalesTeams[
                              salesTeamIndex
                            ]?.user || null
                          }
                          required={true}
                          optionMode={'custom'}
                          renderOption={(props, user: ISalesTeamUser) => (
                            <MenuItem {...props} key={user.id} value={user.id}>
                              <Typography variant="body1">
                                {user.fullName}
                              </Typography>
                            </MenuItem>
                          )}
                          label="New Owner"
                          error={Boolean(
                            !reAssignedTeams[branchIndex]?.filteredSalesTeams[
                              salesTeamIndex
                            ]?.user,
                          )}
                        />

                        {!reAssignedTeams[branchIndex]?.filteredSalesTeams[
                          salesTeamIndex
                        ]?.user && (
                          <FormHelperText error>
                            Select new owner to Re-Assign open records
                          </FormHelperText>
                        )}
                      </Stack>
                    </Stack>
                  </Card>
                )
              },
            )}
          </Stack>
        </Card>
      ))}
    </Stack>
  )
}
